import { PersonList } from '../../../../redux/Candidate/Candidate.type';
import { getUrlBasedOnCurrentPath } from '../../../../components/Modal/Candidate/utils';
import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../../../../utils/getUserRolesByToken';
import TableRowComponent from '../../../../components/Table/TableRow.component';
import { uniqueId } from 'lodash';
import NoResultComponent from '../../../../components/NoResult.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutesConstant } from '../../../../constants/routes.constant';
import { UserPermissions } from '../../../../types/userPermissions.types';
import { dateFormatter } from '../../../../utils/formatters';
import { DATE_TIME_FORMAT } from '../../../../constants/date.contant';
import CandidatePreviewModalComponent from '../../../../components/Modal/Candidate/CandidatePreviewModal.component';
import NotAssignComponent from '../../../../components/NotAssign.component';
import BadgeComponent from '../../../../components/Badge.component';
import { StatusColor } from '../../../../constants/statusColors.constant';
import React from 'react';
import { StyledNameWrapper } from '../Candidates.styled';
import i18n from 'i18next';
import { FavouriteButton } from '../../../../components/Buttons/FavouriteButton/FavouriteButton.component';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { UserRole } from '../../../../types/userRole.types';
import { LocationState } from '../../../../types/locationState.interface';

type CandidatesTableRowProps = {
  handleGetList: (page: number, sort?: string, filters?: any) => void;
  list: {
    items: PersonList[];
  };
  openModal: any;
  permissions: any;
  setFavouriteChanged?: (isFavouriteChanged: boolean) => void;
};

export const CandidatesTableRow = ({
  handleGetList,
  list,
  openModal,
  permissions,
  setFavouriteChanged,
}: CandidatesTableRowProps) => {
  const location = useLocation();
  const { user } = useAuthHook();
  const navigate = useNavigate();

  const prepareRowColumns = (item: PersonList) => {
    const date = item.date ? dateFormatter(item.date, DATE_TIME_FORMAT) : '-';

    return [
      date,
      <StyledNameWrapper>{item.fullName}</StyledNameWrapper>,
      <NotAssignComponent mode="badge" has={!!item.job}>
        {item.job}
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.experience}>
        <BadgeComponent
          appearance="default"
          text={item.experience ? item.experience.name : ''}
        />
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.english}>
        <BadgeComponent
          appearance="default"
          text={item.english ? item.english.level : ''}
        />
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.status}>
        <BadgeComponent
          appearance="default"
          text={item.status!}
          color={
            item.statusColor && i18n.exists(`statusColor.${item.statusColor}`)
              ? `STATUSES_${item.statusColor.toUpperCase()}`
              : item.statusColor || `STATUSES_${StatusColor.GREY.toUpperCase()}`
          }
          textColor={
            item.statusTextColor &&
            i18n.exists(`statusColor.${item.statusTextColor}`)
              ? `STATUSES_${item.statusTextColor.toUpperCase()}`
              : item.statusTextColor ||
                `STATUSES_${StatusColor.GREY.toUpperCase()}`
          }
        />
      </NotAssignComponent>,
      checkUserRequiredRole(user.roles, [UserRole.HEAD, UserRole.DEV_HEAD]) ? (
        <div id="favourite">
          <FavouriteButton
            id={item.id}
            isFavourite={item.isFavourite}
            updateInfo={handleGetList}
            setFavouriteChanged={setFavouriteChanged}
          />
        </div>
      ) : null,
    ];
  };

  const { items } = list;

  const handleCandidateClick = (id: number | string) => {
    openModal(
      CandidatePreviewModalComponent,
      {
        personId: id,
        updateInfo: handleGetList,
      },
      {
        position: 'right-top',
      },
    );
  };

  return items.length > 0 ? (
    items.map((item: PersonList) => {
      const personUrl = getUrlBasedOnCurrentPath(
        location,
        item.id,
        RoutesConstant.CANDIDATES_GROUP,
      );
      const canUserViewCandidate = checkUserRequiredPermissions(permissions, [
        UserPermissions.CAN_VIEW_CANDIDATE,
      ]);
      return (
        <TableRowComponent
          key={uniqueId('table-rows-')}
          columns={prepareRowColumns(item).filter(el => el !== null)}
          to={personUrl}
          handleClick={() =>
            canUserViewCandidate && handleCandidateClick(item.id)
          }
          handleDBClick={() =>
            !!personUrl &&
            canUserViewCandidate &&
            navigate(personUrl, {
              state: {
                backUrl: location.pathname,
                backUrlQuery: location.search,
              } as LocationState,
            })
          }
        />
      );
    })
  ) : (
    <NoResultComponent />
  );
};
